<template>
    <div :class="[
        'bg-black rounded-lg shadow-md text-white', 
        isVerticalLayout ? 'p-4 h-full flex flex-col' : 'p-4 mt-6'
    ]">
        <div class="flex items-center justify-between mb-4">
            <h1 :class="[
                'font-black font-lato text-left uppercase',
                isVerticalLayout ? 'text-lg' : 'text-2xl'
            ]">Gallery
                <span class="rounded-full text-sm bg-white text-black px-2 ml-2">{{ imageList.length }}</span>
            </h1>
            
            <!-- Camera Toggle -->
            <div class="flex items-center gap-2">
                <label class="text-xs text-neutral-400 uppercase tracking-wide">3D Cameras</label>
                <button
                    @click="toggleCameras"
                    :class="[
                        'relative w-10 h-5 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50',
                        showCameras ? 'bg-primary' : 'bg-neutral-600'
                    ]"
                >
                    <div
                        :class="[
                            'absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200',
                            showCameras ? 'translate-x-5' : 'translate-x-0.5'
                        ]"
                    ></div>
                </button>
            </div>
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="text-center">
                <i class="mdi mdi-loading mdi-spin text-3xl text-primary mb-3"></i>
                <p class="text-neutral-400">Loading images...</p>
            </div>
        </div>

        <div v-else-if="hasError" class="flex items-center justify-center py-12">
            <div class="text-center">
                <i class="mdi mdi-alert-circle text-3xl text-red-400 mb-3"></i>
                <p class="text-neutral-400">Unable to load images</p>
            </div>
        </div>

        <!-- Image Gallery - Direct in black container -->
        <div v-else :class="[
            'gap-3',
            isVerticalLayout 
                ? 'grid grid-cols-1 auto-rows-min overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent' 
                : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
        ]">
            <div 
                v-for="(image, index) in displayedImages" 
                :key="image.id"
                :data-image-index="index"
                :class="[
                    'group relative bg-neutral-700 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all duration-200',
                    isVerticalLayout ? 'aspect-square' : 'aspect-square',
                    selectedImageId === image.originalFilename ? 'ring-2 ring-primary' : ''
                ]"
                @click="openLightbox(index)"
                @mouseenter="onImageHover(image)"
                @mouseleave="onImageLeave()"
            >
                <img 
                    :src="getImageUrl(image)"
                    :alt="`Frame ${image.frameNumber}`"
                    class="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    @error="handleImageError"
                >
                <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200"></div>
                <div class="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                    {{ image.frameNumber }}
                </div>
            </div>
        </div>

        <!-- Lightbox Modal -->
        <div 
            v-if="lightboxOpen" 
            class="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            @click="closeLightbox"
        >
            <div class="relative max-w-full max-h-full" @click.stop>
                <img 
                    :src="getImageUrl(imageList[lightboxIndex])"
                    :alt="`Frame ${imageList[lightboxIndex].frameNumber}`"
                    class="max-w-full max-h-full object-contain"
                >
                
                <!-- Navigation Controls -->
                <button 
                    v-if="lightboxIndex > 0"
                    @click="previousImage"
                    class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                    <i class="mdi mdi-chevron-left text-2xl"></i>
                </button>
                
                <button 
                    v-if="lightboxIndex < imageList.length - 1"
                    @click="nextImage"
                    class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                    <i class="mdi mdi-chevron-right text-2xl"></i>
                </button>
                
                <!-- Close Button -->
                <button 
                    @click="closeLightbox"
                    class="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                >
                    <i class="mdi mdi-close text-2xl"></i>
                </button>
                
                <!-- Image Info -->
                <div class="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
                    <div class="font-medium">Frame {{ imageList[lightboxIndex].frameNumber }}</div>
                    <div class="text-sm text-neutral-300">{{ lightboxIndex + 1 }} of {{ imageList.length }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { EventBus } from '../event-bus';
import { storage_resto } from '../main';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

export default {
    name: 'SurveyPhotos',
    props: {
        dataPath: {
            type: String,
            default: null
        },
        isVerticalLayout: {
            type: Boolean,
            default: false
        },
        showOverlays: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            imageList: [],
            isLoading: true,
            hasError: false,
            lightboxOpen: false,
            lightboxIndex: 0,
            selectedImageId: null,
            showCameras: false
        };
    },
    mounted() {
        this.loadImages();
        
        // Listen for camera selection events
        EventBus.$on('camera-selected', this.onCameraSelected);
        EventBus.$on('camera-deselected', this.onCameraDeselected);
        EventBus.$on('cameras-visibility-changed', this.onCamerasVisibilityChanged);
    },
    computed: {
        displayedImages() {
            return this.imageList;
        },
    },
    methods: {
        async loadImages() {
            try {
                this.isLoading = true;
                this.hasError = false;
                
                // Dynamically discover available images
                const images = await this.discoverAvailableImages();
                this.imageList = images;
                
            } catch (error) {
                console.error('Error loading images:', error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },
        
        async discoverAvailableImages() {
            // Load images from Firebase Storage using COLMAP images.txt as manifest
            try {
                const imageNames = await this.loadImageNamesFromColmap();
                return await this.processImageNames(imageNames);
            } catch (error) {
                console.error('Could not load images from Firebase Storage:', error);
                throw error;
            }
        },
        
        async loadImageNamesFromColmap() {
            // Load the COLMAP images.txt file from Firebase Storage
            const storagePath = `${this.dataPath}/output/colmap/sparse/0/images.txt`;
            console.log('Loading image list from:', storagePath);
            
            const fileRef = storageRef(storage_resto, storagePath);
            const downloadUrl = await getDownloadURL(fileRef);
            
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error('Could not load images.txt');
            }
            
            const text = await response.text();
            const lines = text.split('\n');
            const imageNames = [];
            
            // Parse COLMAP images.txt format
            // Format: IMAGE_ID, QW, QX, QY, QZ, TX, TY, TZ, CAMERA_ID, NAME
            for (const line of lines) {
                if (line.startsWith('#') || line.trim() === '') continue;
                
                const parts = line.trim().split(/\s+/);
                if (parts.length >= 10) {
                    const imageName = parts[9]; // The filename is the last part
                    if (imageName && imageName.endsWith('.jpg')) {
                        imageNames.push(imageName);
                    }
                }
            }
            
            return imageNames;
        },
        
        async processImageNames(imageNames) {
            const images = [];
            
            // Sort image names to ensure proper ordering
            imageNames.sort();
            
            // Fetch Firebase Storage URLs for all images
            const urlPromises = imageNames.map(async (filename, index) => {
                try {
                    // Extract frame number from filename if possible
                    const frameMatch = filename.match(/_(\d+)\.jpg$/);
                    const frameNumber = frameMatch ? parseInt(frameMatch[1]) : index;
                    
                    // Generate base filename without extension
                    const baseFilename = filename.replace('.jpg', '');
                    
                    // Get Firebase Storage URLs
                    const originalPath = `${this.dataPath}/output/colmap/images/${filename}`;
                    const overlayPath = `${this.dataPath}/output/overlays/${baseFilename}_overlay.png`;
                    
                    const originalUrl = await getDownloadURL(storageRef(storage_resto, originalPath));
                    
                    // Try to get overlay URL, but don't fail if it doesn't exist
                    let overlayUrl = null;
                    try {
                        overlayUrl = await getDownloadURL(storageRef(storage_resto, overlayPath));
                    } catch (error) {
                        console.warn(`Overlay not found for ${filename}`);
                    }
                    
                    return {
                        id: index,
                        frameNumber: frameNumber,
                        originalFilename: filename,
                        overlayFilename: `${baseFilename}_overlay.png`,
                        originalUrl: originalUrl,
                        overlayUrl: overlayUrl
                    };
                } catch (error) {
                    console.error(`Error loading URLs for ${filename}:`, error);
                    return null;
                }
            });
            
            const results = await Promise.all(urlPromises);
            const validImages = results.filter(img => img !== null);
            
            return validImages.sort((a, b) => a.frameNumber - b.frameNumber);
        },
        
        getImageUrl(image) {
            if (this.showOverlays && image.overlayUrl) {
                return image.overlayUrl;
            } else {
                return image.originalUrl;
            }
        },
        
        handleImageError(event) {
            console.warn('Failed to load image:', event.target.src);
            event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0iY2VudHJhbCIgZmlsbD0iIzlDQTNBRiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiPkltYWdlIG5vdCBmb3VuZDwvdGV4dD4KPHN2Zz4K';
        },
        
        openLightbox(index) {
            this.lightboxIndex = index;
            this.lightboxOpen = true;
            document.body.style.overflow = 'hidden';
        },
        
        closeLightbox() {
            this.lightboxOpen = false;
            document.body.style.overflow = '';
        },
        
        previousImage() {
            if (this.lightboxIndex > 0) {
                this.lightboxIndex--;
            }
        },
        
        nextImage() {
            if (this.lightboxIndex < this.imageList.length - 1) {
                this.lightboxIndex++;
            }
        },

        onImageHover(image) {
            // Emit event to 3D view to highlight corresponding camera
            EventBus.$emit('gallery-image-hover', {
                filename: image.originalFilename,
                frameNumber: image.frameNumber
            });
        },

        onImageLeave() {
            // Emit event to 3D view to remove camera highlight
            EventBus.$emit('gallery-image-leave');
        },

        onCameraSelected(data) {
            // Scroll to corresponding image in gallery
            this.selectedImageId = data.cameraLabel; // cameraLabel contains the filename
            this.scrollToImage(data.cameraLabel);
        },

        onCameraDeselected() {
            this.selectedImageId = null;
        },

        scrollToImage(filename) {
            // Find the image in our list
            const imageIndex = this.imageList.findIndex(img => img.originalFilename === filename);
            if (imageIndex === -1) return;

            // Find the DOM element and scroll to it
            this.$nextTick(() => {
                const imageElements = this.$el.querySelectorAll('[data-image-index]');
                if (imageElements[imageIndex]) {
                    imageElements[imageIndex].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            });
        },

        toggleCameras() {
            // Emit toggle event to 3D model component
            EventBus.$emit('toggle-cameras');
        },

        onCamerasVisibilityChanged(visible) {
            this.showCameras = visible;
        }
    },
    
    watch: {
        dataPath: {
            handler() {
                // When dataPath changes, reload images
                this.loadImages();
            },
            immediate: false
        }
    },
    
    beforeDestroy() {
        // Clean up any lightbox overflow changes
        document.body.style.overflow = '';
        
        // Remove event bus listeners
        EventBus.$off('camera-selected', this.onCameraSelected);
        EventBus.$off('camera-deselected', this.onCameraDeselected);
        EventBus.$off('cameras-visibility-changed', this.onCamerasVisibilityChanged);
    }
};
</script>

<style scoped>
.text-primary {
    color: #27bdf4;
}

/* Hide scrollbar for lightbox */
.overflow-hidden {
    overflow: hidden !important;
}

/* Custom scrollbar styles for vertical layout */
.scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #4b5563 transparent;
}

.scrollbar-thin::-webkit-scrollbar {
    width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: #4b5563;
    border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background-color: #6b7280;
}
</style>
